import { useState, useRef, useEffect, useMemo } from "react";
import Livehighlight from "./livehighlight/Livehighlight";
import "./Livescore.css";
import logo from "./livescoreImage/bray.png";
import star from "./../../images/star.png";
import blackArrow from "./../../images/arrow.png";
import whiteArrow from "./../../images/white-arrow.png";
import { useParams, useNavigate, NavLink } from "react-router-dom";
import calander from "./calendar.png";
import search from "./Search.png";
import { Helmet } from "react-helmet-async";
import worldCup from "../home/HomeImage/worldcup.png";

const LivescoreDate = ({ dark, MyBaseUrl }) => {
  const { date } = useParams();
  const navigate = useNavigate();

  const createSlug = (home, away) => {
    return `${home}-${away}`
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]+/g, "");
  };

  const [showSearch, setShowSearch] = useState(false);

  const dateRef = useRef(null);

  const formatDate = (d) => {
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const formatDDMMYYYY = (d) => {
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const [liveToday, setLiveToday] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveToday(new Date());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const todayStr = formatDate(liveToday);
  const [currentDate, setCurrentDate] = useState(date || todayStr);

  useEffect(() => {
    const interval = setInterval(() => {
      const newToday = formatDate(new Date());

      setCurrentDate((prev) => {
        if (!date && prev !== newToday) {
          navigate(`/matches/${newToday}`);
          return newToday;
        }
        return prev;
      });
    }, 60000);

    return () => clearInterval(interval);
  }, [date, navigate]);

  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (date) setCurrentDate(date);
  }, [date]);

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(`${MyBaseUrl}/matches?date=${currentDate}`);

        const data = await res.json();
        setMatches(data?.data || []);
      } catch (err) {
        setError("Failed to fetch matches");
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [currentDate]);

  const changeDate = (amount) => {
    const parts = currentDate.split("-").map(Number);
    const newDate = new Date(parts[0], parts[1] - 1, parts[2] + amount);
    const formatted = formatDate(newDate);

    setShowLiveOnly(false);
    setCurrentDate(formatted);

    navigate(`/matches/${formatted}`);
  };

  const isLiveMatch = (match) => {
    const s = match?.fixture?.status;

    return (
      s?.short !== "FT" &&
      s?.short !== "NS" &&
      s?.short !== "PST" &&
      s?.short !== "CANC" &&
      (s?.elapsed ?? 0) <= 90
    );
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (!value) return;

    setShowLiveOnly(false);
    setCurrentDate(value);
    navigate(`/matches/${value}`);
  };

  const getRelativeLabel = (dateStr) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    const selected = new Date(y, m - 1, d);

    const today = new Date(
      liveToday.getFullYear(),
      liveToday.getMonth(),
      liveToday.getDate(),
    );

    const diffTime = selected.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays === -1) return "Yesterday";

    return selected.toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "2-digit",
    });
  };
  const displayLabel = getRelativeLabel(currentDate);

  const groupedLeagues = useMemo(() => {
    const map = {};

    matches.forEach((match) => {
      const leagueId = match?.league?.id;
      if (!leagueId) return;

      if (!map[leagueId]) {
        map[leagueId] = {
          id: leagueId,
          league: match.league.name,
          country: match.league.country,
          logo: match.league.logo,
          matches: [],
        };
      }

      map[leagueId].matches.push(match);
    });

    return Object.values(map);
  }, [matches]);

  const leagues = groupedLeagues;

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const [showLeague, setShowLeague] = useState(false);
  const [selectedComp, setSelectedComp] = useState(null);
  const [dropLeague, setDropLeague] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);
  const [isBelow600, setIsBelow600] = useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 700;
      const below600 = window.innerWidth <= 600;
      setIsMobile(mobile);
      setIsBelow600(below600);
      if (!mobile) setDropLeague(true);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const leagueRefs = useRef([]);

  const handleNavbarClick = (index) => {
    leagueRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
    setDropLeague((prev) => !prev);
  };

  const [showMenu, setShowMenu] = useState(true);

  const handleClick = (league) => {
    setSelectedComp(league);
    setShowLeague(true);
    setShowMenu(false);
  };

  const handleBack = () => {
    setShowLeague(false);
    setSelectedComp(null);
    setShowMenu(true);
  };

  const handleDrop = () => {
    if (!isMobile) return;
    setDropLeague((prev) => !prev);
  };

  const condivStyle = dark
    ? { backgroundColor: isBelow600 ? "transparent" : "#111111e7" }
    : { backgroundColor: isBelow600 ? "transparent" : "white" };

  const [myFav, setMyFav] = useState({});
  const favorite = (matchId) => {
    setMyFav((prev) => ({
      ...prev,
      [matchId]: !prev[matchId],
    }));
  };

  const [showLiveOnly, setShowLiveOnly] = useState(false);

  const toggleLiveFilter = () => {
    const today = formatDate(new Date());

    setCurrentDate(today);
    navigate(`/matches/${today}`);

    setShowLiveOnly((prev) => !prev);
  };

  const [searchQuery, setSearchQuery] = useState("");

  const filteredLeagues = useMemo(() => {
    return leagues
      .map((l) => {
        let filteredMatches = l.matches;

        if (showLiveOnly) {
          filteredMatches = filteredMatches.filter((match) => {
            const status = match?.fixture?.status?.short;
            const elapsed = Number(match?.fixture?.status?.elapsed);

            return (
              status !== "FT" &&
              status !== "NS" &&
              status !== "PST" &&
              status !== "CANC" &&
              (status === "1H" ||
                status === "2H" ||
                status === "HT" ||
                (elapsed > 0 && elapsed < 120))
            );
          });
        }

        if (searchQuery.trim() !== "") {
          const q = searchQuery.toLowerCase();

          filteredMatches = filteredMatches.filter((match) => {
            const home = match?.teams?.home?.name?.toLowerCase() || "";
            const away = match?.teams?.away?.name?.toLowerCase() || "";
            const leagueName = l.league?.toLowerCase() || "";

            return (
              home.includes(q) || away.includes(q) || leagueName.includes(q)
            );
          });
        }

        return {
          ...l,
          matches: filteredMatches,
        };
      })
      .filter((l) => l.matches.length > 0);
  }, [leagues, showLiveOnly, searchQuery]);

  return (
    <div className="homer">
      <Helmet>
        <meta
          name="description"
          content="Follow live football scores, match updates, fixtures, results, and real-time league action from around the world."
        />
        <meta
          name="keywords"
          content="live scores, football live, match results, fixtures, soccer scores, today matches, sports updates"
        />
        <meta property="og:title" content="Live Scores | Goal Arena" />
        <meta
          property="og:description"
          content="Get real-time football match updates, scores, and fixtures across all major leagues."
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="livescoreWrapper">
        <div
          className="searchhead"
          style={
            dark
              ? { color: "#fafafaff", backgroundColor: "#272727ff" }
              : { color: "#161819", backgroundColor: "whitesmoke" }
          }>
          <h1>
            {showMenu && <span onClick={handleDrop}>&#9776;</span>} Livescore
          </h1>

          <div
            className="search-container"
            style={{
              transform: "translateX(10px)",
              display: "flex",
              alignItems: "center",
              position: "relative",
            }}>
            <input
              type="search"
              placeholder="Search for match"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              style={{
                width: showSearch ? "300px" : "0px",
                opacity: showSearch ? 1 : 0,
                padding: showSearch ? "10px 40px 10px 12px" : "10px 0px",
                transition: "all 0.35s ease",
                overflow: "hidden",
                border: showSearch ? "1px solid #555" : "none",
                backgroundColor: dark ? "#1b1b1b" : "#fff",
                color: dark ? "#fff" : "#000",
              }}
            />

            <div
              className="search-icon"
              onClick={() => setShowSearch((prev) => !prev)}
              style={{
                position: "absolute",
                right: "10px",
                cursor: "pointer",
                zIndex: 2,
              }}>
              <img src={search} alt="" className="ser" />
            </div>
          </div>
        </div>

        <section className="firstsection">
          {(isMobile ? dropLeague : true) && (
            <nav
              className="navbar"
              style={
                dark
                  ? {
                      backgroundColor: isMobile ? "#272727ff" : "#111111ff",
                      boxShadow: "none",
                    }
                  : { backgroundColor: isMobile && "whitesmoke" }
              }>
              <p
                className={`leaguehead ${dark ? "darkhead" : "lighthead"}`}
                style={
                  dark
                    ? { backgroundColor: "#111111ff", color: "#fafafaff" }
                    : {
                        borderBottom: isMobile ? "1px solid #cfcfcfff" : "none",
                        color: "#161819",
                        backgroundColor: isMobile ? "whitesmoke" : "white",
                      }
                }>
                All Leagues{" "}
                <strong onClick={() => setDropLeague(false)}>X</strong>
              </p>

              <div>
                {filteredLeagues.map((league, index) => (
                  <span
                    key={league.id}
                    onClick={() => handleNavbarClick(index)}>
                    <img
                      src={league?.id === 1 ? worldCup : league.logo || logo}
                      alt=""
                    />
                    <p
                      style={
                        dark ? { color: "#fafafaff" } : { color: "#161819" }
                      }>
                      {league.league}
                    </p>
                  </span>
                ))}
              </div>
            </nav>
          )}

          <div className="condiv" style={condivStyle}>
            {!showLeague && (
              <div
                className="leaguedate"
                style={
                  dark
                    ? { backgroundColor: "transparent", color: "#fafafaff" }
                    : { backgroundColor: "transparent" }
                }>
                <button
                  onClick={toggleLiveFilter}
                  style={{
                    backgroundColor: showLiveOnly ? "#86efac" : "",
                    color: showLiveOnly
                      ? "#161819"
                      : dark
                        ? "#fafafa"
                        : "#161819",
                    transition: "0.3s ease",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}>
                  <p
                    style={{
                      backgroundColor: showLiveOnly ? "#16a34a" : "",
                      transition: "0.3s ease",
                      cursor: "pointer",
                    }}></p>{" "}
                  LIVE
                </button>

                <div className="dateWrapper">
                  <span
                    className="relativeDate"
                    style={{
                      ...(dark ? { color: "#fafafaff" } : { color: "#161819" }),
                      pointerEvents: "none",
                    }}>
                    {displayLabel}
                  </span>

                  <div>
                    <input
                      ref={dateRef}
                      className={`dated ${dark ? "mydark" : "mylight"}`}
                      type="date"
                      value={currentDate || ""}
                      onChange={handleInputChange}
                    />

                    <span
                      className="calander"
                      onClick={() => dateRef.current?.showPicker()}
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        fontSize: "18px",
                        zIndex: "100",
                      }}>
                      <img
                        src={calander}
                        alt=""
                        style={dark ? { filter: "invert(1)" } : {}}
                      />
                    </span>
                  </div>

                  {dark ? (
                    <span className="nxt">
                      <img
                        src={whiteArrow}
                        alt=""
                        className="wtt"
                        style={{ transform: "rotate(180deg)" }}
                        onClick={() => changeDate(-1)}
                      />
                      <img
                        src={whiteArrow}
                        alt=""
                        className="wtt"
                        onClick={() => changeDate(1)}
                      />
                    </span>
                  ) : (
                    <span className="nxt">
                      <img
                        src={blackArrow}
                        alt=""
                        className="blk"
                        onClick={() => changeDate(-1)}
                      />
                      <img
                        src={blackArrow}
                        alt=""
                        className="blk"
                        style={{ transform: "rotate(180deg)" }}
                        onClick={() => changeDate(1)}
                      />
                    </span>
                  )}
                </div>
              </div>
            )}

            {!showLeague && (
              <div className="kkk">
                {loading && (
                  <div className=" load">
                    {[1, 2, 3].map((_, leagueIndex) => (
                      <section className="skeleton" key={leagueIndex}>
                        {[1, 2, 3, 4].map((_, matchIndex) => (
                          <div
                            className={` skeleton-match`}
                            style={{
                              backgroundColor: dark ? "#383838e7" : "#f0f0f0ff",
                            }}
                            key={matchIndex}>
                            <p
                              style={{
                                backgroundColor: dark
                                  ? "#757575e7"
                                  : "#7c7c7c6b",
                              }}></p>
                          </div>
                        ))}
                      </section>
                    ))}
                  </div>
                )}

                {error && (
                  <p
                    style={
                      dark
                        ? { color: "#dfdfdfff", marginTop: "30px" }
                        : { color: "#3e3f3f", marginTop: "30px" }
                    }>
                    {error}
                  </p>
                )}
                {filteredLeagues.map((league, index) =>
                  loading ? (
                    <p></p>
                  ) : (
                    <section
                      className="secondsection"
                      key={league.id}
                      ref={(el) => (leagueRefs.current[index] = el)}>
                      <NavLink
                        className="navy"
                        to={`/competition/${
                          league?.league
                            ? league.league
                                .replace(/\//g, "")
                                .replace(/\s+/g, "-")
                                .toLowerCase()
                            : "league"
                        }/${league?.id}`}
                        style={{
                          textDecoration: "none",
                          display: "flex",
                          flexDirection: "row",
                          width: "100%",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}>
                        <div>
                          <div>
                            <img
                              src={
                                league?.id === 1
                                  ? worldCup
                                  : league.logo || logo
                              }
                              alt=""
                            />
                            <span>
                              <h4
                                style={
                                  dark
                                    ? { color: "#fafafaff" }
                                    : { color: "#161819" }
                                }>
                                {league.league}
                              </h4>
                              <small
                                style={
                                  dark
                                    ? { color: "#d6d4d4ff" }
                                    : { color: "#3F3E3E" }
                                }>
                                {league.country}
                              </small>
                            </span>
                          </div>

                          <b>
                            {!dark ? (
                              <img
                                src={blackArrow}
                                alt=""
                                style={{
                                  transform: "rotate(180deg)",
                                  scale: "0.9",
                                }}
                              />
                            ) : (
                              <img
                                src={whiteArrow}
                                alt=""
                                style={{ width: "15px", scale: "0.9" }}
                              />
                            )}
                          </b>
                        </div>
                      </NavLink>
                      {league.matches.map((match, mIndex) => (
                        <div
                          className="onmatch"
                          style={
                            dark
                              ? {
                                  backgroundColor: "#1f1f1fe7",
                                  border: "1px solid #333",
                                }
                              : {}
                          }>
                          <NavLink
                            key={match?.fixture?.id}
                            to={`/matches/${createSlug(
                              match?.teams?.home?.name || "home",
                              match?.teams?.away?.name || "away",
                            )}/${match?.fixture?.id}`}
                            state={{ matchId: match?.fixture?.id }}
                            style={{
                              width: "100%",
                              display: "flex",
                              gap: "10px",
                            }}>
                            <span>
                              <h3
                                className={
                                  match?.fixture?.status?.elapsed > 0 &&
                                  match?.fixture?.status?.elapsed < 90 &&
                                  match?.fixture?.status?.short !== "FT"
                                    ? "live-time"
                                    : ""
                                }
                                style={
                                  match?.fixture?.status?.elapsed > 0 &&
                                  match?.fixture?.status?.elapsed < 90 &&
                                  match?.fixture?.status?.short !== "FT"
                                    ? {}
                                    : dark
                                      ? { color: "#dfdfdfff" }
                                      : { color: "#3e3f3f" }
                                }>
                                {match?.score?.penalty?.home !== null ||
                                match?.score?.penalty?.away !== null
                                  ? "PK"
                                  : match?.score?.extratime?.home !== null ||
                                      match?.score?.extratime?.away !== null
                                    ? "Extra"
                                    : match?.fixture?.status?.short === "FT"
                                      ? "FT"
                                      : match?.fixture?.status?.elapsed
                                        ? `${
                                            match.fixture.status.elapsed >=
                                              90 && match.fixture.status.extra
                                              ? `${match.fixture.status.elapsed}<sup>+${match.fixture.status.extra}</sup>`
                                              : match.fixture.status.elapsed
                                          }'`
                                        : match?.fixture?.status?.short === "NS"
                                          ? new Date(
                                              match?.fixture?.date,
                                            ).toLocaleTimeString([], {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                              hour12: false,
                                            })
                                          : match?.fixture?.status?.short}
                              </h3>
                            </span>

                            <div className="league">
                              <nav>
                                <img
                                  src={match?.teams?.home?.logo || logo}
                                  alt=""
                                />
                                <h2 style={dark ? { color: "#fafafaff" } : {}}>
                                  {match?.teams?.home?.name}
                                </h2>
                              </nav>

                              <nav>
                                <img
                                  src={match?.teams?.away?.logo || logo}
                                  alt=""
                                />
                                <h2 style={dark ? { color: "#fafafaff" } : {}}>
                                  {match?.teams?.away?.name}
                                </h2>
                              </nav>
                            </div>
                          </NavLink>

                          {match?.fixture?.status?.short !== "NS" &&
                            match?.fixture?.status?.short !== "CANC" && (
                              <div className="scores">
                                <strong
                                  style={
                                    dark
                                      ? { color: "#dfdfdfff" }
                                      : { color: "#3e3f3f" }
                                  }>
                                  {match?.goals?.home ?? 0}
                                </strong>

                                <strong
                                  style={
                                    dark
                                      ? { color: "#dfdfdfff" }
                                      : { color: "#3e3f3f" }
                                  }>
                                  {match?.goals?.away ?? 0}
                                </strong>
                              </div>
                            )}

                          <img
                            src={star}
                            alt=""
                            className={
                              myFav[match?.fixture?.id] ? "star" : "nostar"
                            }
                            onClick={() => favorite(match?.fixture?.id)}
                          />
                        </div>
                      ))}
                    </section>
                  ),
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default LivescoreDate;
